import Gleap from 'gleap';
import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import { store } from './store'
import PrimeVue from 'primevue/config';
import VirtualScroller from 'primevue/virtualscroller';
import Calendar from 'primevue/calendar';
import VCalendar from 'v-calendar';
import Chip from 'primevue/chip';
import MultiSelect from 'primevue/multiselect';
import Tooltip from 'primevue/tooltip';
import TreeSelect from 'primevue/treeselect';
import TreeTable from 'primevue/treetable';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import Menu from 'primevue/menu';
import TieredMenu from 'primevue/tieredmenu';
import MegaMenu from 'primevue/megamenu';
import PanelMenu from 'primevue/panelmenu';
import ContextMenu from 'primevue/contextmenu';
import InputNumber from 'primevue/inputnumber';
import InputMask from 'primevue/inputmask';
import ProgressBar from 'primevue/progressbar';
import Textarea from 'primevue/textarea';
import RadioButton from 'primevue/radiobutton';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Password from 'primevue/password';
import InputSwitch from 'primevue/inputswitch';
import SelectButton from 'primevue/selectbutton';
import ToggleButton from 'primevue/togglebutton';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import axios from 'axios';
import VueAxios from 'vue-axios';
import VueApexCharts from "vue3-apexcharts";
import Sidebar from 'primevue/sidebar';
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import './assets/css/main.scss'
// import VueCal from 'vue-cal'
// import VueCookies from 'vue-cookies'
import VueGoogleAutocomplete from 'vue-google-autocomplete';
import "primevue/resources/themes/saga-blue/theme.css"       //theme
import "primevue/resources/primevue.min.css"                 //core css
import "primeicons/primeicons.css"                           //icons
// import 'vue-cal/dist/vuecal.css'
import tooltip from "./directives/tooltip.js";
import clickoutside from "./directives/clicktodismiss.js"


// SENTRY
import * as Sentry from "@sentry/vue";
import { Integrations } from "@sentry/tracing";

// .use(VueCal)

const app = createApp(App);

app.config.globalProperties.$resident = function (options = {}) {
  let isPlural = false;
  let capitalize = false;
  let uppercase = false;
  
  if (typeof options === 'boolean') {
    isPlural = options;
  } else if (options && typeof options === 'object') {
    isPlural = !!options.plural;
    capitalize = !!options.capitalize;
    uppercase = !!options.uppercase;
  }
  
  const facility = this.$store.state.currentLocation?.facility;
  let word = facility?.naming_convention?.name || 'resident';
  
  // Normalize to lowercase
  word = word.toLowerCase();
  
  if (isPlural) {
    if (word.endsWith('s') || word.endsWith('x') || word.endsWith('z') || word.endsWith('ch') || word.endsWith('sh')) {
      word = word + 'es';
    } else if (word.endsWith('y')) {
      const vowelBeforeY = ['a', 'e', 'i', 'o', 'u'].includes(word.charAt(word.length - 2));
      if (vowelBeforeY) {
        word = word + 's';
      } else {
        word = word.slice(0, -1) + 'ies';
      }
    } else {
      word = word + 's';
    }
  }
  
  if (uppercase) {
    word = word.toUpperCase();
  } else if (capitalize) {
    word = word.replace(/\b\w/g, c => c.toUpperCase());
  }
  
  return word;
};

// Global handler for uncaught errors thrown inside components (render, watchers, lifecycle
// hooks, event handlers).
//
// Installing this stops Vue re-throwing to `window.onerror`, so anything that used to be
// picked up there has to be forwarded from here explicitly - otherwise component crashes
// become console-only. `info` is Vue's description of where the error came from (e.g.
// "render function", "watcher callback"), which is usually more useful than the stack alone.
//
// This must be assigned *before* Sentry.init(): the Vue integration replaces
// `app.config.errorHandler` with its own wrapper that captures the exception and then calls
// whatever handler was already there. So Sentry does the reporting (with component name and
// trace attached) and this only handles the local console output - capturing here as well
// would double-report every error. Note console.* is stripped from staging/production builds
// by babel-plugin-transform-remove-console, so in deployed builds this is a no-op and Sentry
// is the only consumer.
app.config.errorHandler = (err, vm, info) => {
  const component = vm?.$options?.name || vm?.$options?.__name || vm?.$?.type?.name || 'unknown component';
  console.error(`[vue] error in ${component} (${info}):`, err);
};

// Which deployment this build is running on, derived from the hostname the same way the app
// derives `subdomain` (see Login.vue / Navbar.vue) so Sentry's environment tag lines up with
// what the rest of the app thinks it is. Order matters: staging.strongcare.ai is staging.
const deploymentEnvironment = () => {
  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'local';

  const [first, second] = hostname.split('.');
  if (first === 'staging' || second === 'stage') return 'staging';
  if (first === 'care' || second === 'strongcare') return 'production';
  return 'staging';
};

const environment = deploymentEnvironment();

// Every axios failure has the same top frame - `settle()` inside the library - and no first-party
// frame at all, so Sentry collapsed all of them into one issue per status code. One group held
// 190,000 events across 590 users with nothing in it to say which endpoint was failing.
//
// These three tags plus the fingerprint split that group per endpoint. The route is normalised
// first: raw request paths carry patient and prescription uuids, and `attachProps: false` above
// exists precisely to keep identifiers like those out of Sentry. Anything that looks like an id
// becomes `:id`, and query strings are dropped entirely.
const ROUTE_SHAPE = [
  [/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id'],
  [/\/\d+/g, '/:id'],
];

const routeShape = (url) => {
  if (typeof url !== 'string') return null;
  // Relative in this app, but a full URL would put the host in the tag - keep the path only.
  let path = url.split('?')[0].split('#')[0].replace(/^https?:\/\/[^/]+/i, '');
  ROUTE_SHAPE.forEach(([pattern, replacement]) => { path = path.replace(pattern, replacement); });
  return path;
};

// `isAxiosError` is set by axios itself on every error it rejects with, in both 0.x and 1.x.
const axiosDetail = (error) => {
  if (!error || error.isAxiosError !== true) return null;
  const method = error.config && error.config.method ? String(error.config.method).toUpperCase() : null;
  const route = routeShape(error.config && error.config.url);
  const status = error.response && error.response.status ? String(error.response.status) : null;
  return (method || route || status) ? { method, route, status } : null;
};

// The Gleap support widget rejects its own session promise with no value whenever
// api.gleap.io answers 401, which it does on every login until the session is re-identified.
// Sentry captures that as "Non-Error promise rejection captured with value: undefined" with no
// stack, no message and no breadcrumb pointing at our code - 1,451 events with nothing in them
// to act on. Anything thrown by this app arrives with either a stack or a real error value, so
// dropping the stackless undefined rejections costs no signal of ours.
const isBareRejection = (event) => {
  const exception = event && event.exception && event.exception.values && event.exception.values[0];
  if (!exception) return false;
  if (exception.value !== 'Non-Error promise rejection captured with value: undefined') return false;
  const frames = exception.stacktrace && exception.stacktrace.frames;
  return !frames || frames.length === 0;
};

// Sentry has to be initialised before mount() - the SDK attaches its error handler and the
// tracing mixin to the app at init time, so anything that runs earlier (including the entire
// first render) is invisible to it.
//
// Wrapped because init() now sits on the critical path to mount(): a throw in here would take
// the whole app down, and error reporting failing is never worth that.
try {
  Sentry.init({
    // Vue 3 expects the app *instance* here. The previous `App` (the root component) matched
    // neither `app` nor `Vue`, so init() bailed out early and the Vue integration - component
    // names, error handler, router tracing - never attached at all.
    app,
    dsn: "https://bb0d75e1063e4f00bac76315325b83ba@o981937.ingest.sentry.io/5967154",
    environment,
    // Local dev errors are noise in the issue stream and burn quota. The integration still
    // attaches (so the console handler above keeps working), it just doesn't transmit.
    enabled: environment !== 'local',
    // Component props routinely hold patient, prescription and administration data. Never
    // ship that to a third party.
    attachProps: false,
    integrations: [
      new Integrations.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        // This list only decides which outgoing requests get a `sentry-trace` header: entries
        // are substring-matched (strings) or regex-tested against the request URL as passed to
        // XHR/fetch. Every API call in this app is a leading-slash relative path, so `/^\//`
        // covers the whole backend. A `frontend-load-balancer-*.elb.amazonaws.com` entry used
        // to sit here and matched nothing - no request URL in the app contains that hostname -
        // so it was removed rather than left implying propagation that wasn't happening.
        //
        // Note this does NOT give us a distributed trace into the Rust backend, and never did:
        // rust-webserver creates no Sentry transactions (no sentry-tower layer), and the trace
        // context it *does* continue is a W3C `traceparent`, converted to B3 and forwarded to
        // Kraken via opentelemetry_zipkin for Splunk (see src/middleware/makespan.rs and
        // src/kraken.rs). @sentry/vue only ever emits `sentry-trace`. Joining the two is its
        // own piece of work - see DEFERRED_REVIEW_ITEMS.md 5.6.
        tracingOrigins: ["localhost", /^\//],
      }),
    ],
    // 100% tracing in production is a lot of quota for very little extra signal.
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    // Runs on the way out, after the SDK has built the event. Never throw from here - a throw
    // drops the event, and it would drop every event, not just the axios ones.
    beforeSend(event, hint) {
      try {
        if (isBareRejection(event)) return null;

        const detail = axiosDetail(hint && hint.originalException);
        if (!detail) return event;

        event.tags = { ...event.tags };
        if (detail.method) event.tags['api.method'] = detail.method;
        if (detail.route) event.tags['api.route'] = detail.route;
        if (detail.status) event.tags['api.status'] = detail.status;

        // '{{ default }}' keeps Sentry's own grouping and adds the endpoint on top, so an issue
        // still splits by error type as well as by the call that failed.
        event.fingerprint = ['{{ default }}', detail.method, detail.route, detail.status].filter(Boolean);
      } catch (beforeSendError) {
        console.error('[sentry] beforeSend failed:', beforeSendError);
      }
      return event;
    },
  });
} catch (sentryInitError) {
  console.error('[sentry] failed to initialise:', sentryInitError);
}

app.use(router).directive("tooltip", tooltip).use(clickoutside).use(VueAxios, axios).use(VueApexCharts).use(PrimeVue).use(store).use(VCalendar, {}).component('Calendar', Calendar).use(ToastService).component('Toast', Toast)
  .component('RadioButton', RadioButton).component('Button', Button).component('Menu', Menu).component('TieredMenu', TieredMenu).component('MegaMenu', MegaMenu).component('ContextMenu', ContextMenu).component('PanelMenu', PanelMenu)
  .component('Password', Password).component('ToggleButton', ToggleButton).component('SelectButton', SelectButton).component('VirtualScroller', VirtualScroller).component('Checkbox', Checkbox).component('Dropdown', Dropdown).component('InputSwitch', InputSwitch)
  .component('MultiSelect', MultiSelect).component('InputText', InputText).component('InputNumber', InputNumber).component('InputMask', InputMask).component('TreeSelect', TreeSelect).component('TreeTable', TreeTable)
  .component('DataTable', DataTable).component('Column', Column).component('TabView', TabView).component('TabPanel', TabPanel)
  .component('Textarea', Textarea).component('VueGoogleAutocomplete', VueGoogleAutocomplete).component('Sidebar', Sidebar).component('Chip', Chip).component('ProgressBar', ProgressBar)

app.mount('#app')

Gleap.initialize('1lcvpeNsGWuVRJFuNkKkjYQulpYEqi9b');

// Reset scroll position and focus of PrimeVue dropdown inputs on selection
const resetDropdowns = () => {
  setTimeout(() => {
    const activeEl = document.activeElement;
    if (activeEl && activeEl.tagName === 'INPUT' && activeEl.classList.contains('p-dropdown-label')) {
      activeEl.scrollLeft = 0;
      activeEl.blur();
    }
    document.querySelectorAll('.p-dropdown-label.p-inputtext').forEach(input => {
      input.scrollLeft = 0;
    });
  }, 100);
};

document.addEventListener('click', (event) => {
  if (event.target.closest('.p-dropdown-item')) {
    resetDropdowns();
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Enter') {
    const activeEl = document.activeElement;
    if (activeEl && activeEl.tagName === 'INPUT' && activeEl.classList.contains('p-dropdown-label')) {
      resetDropdowns();
    }
  }
});

document.addEventListener('focusout', (event) => {
  if (event.target && event.target.tagName === 'INPUT' && event.target.classList.contains('p-dropdown-label')) {
    event.target.scrollLeft = 0;
  }
}, true);